import * as yup from 'yup'
import { FormValues } from 'screens/Campaign/Overview/EditChannelsModal/Types'
import { BrandPlanTactic, ContentCount, WeekDays } from 'types'

const contentFields: (keyof Omit<
  FormValues,
  'video_crosspost_contents' | 'crosspost_contents' | 'crosspost_frequency' | 'tactics'
>)[] = [
  'instagram_contents',
  'facebook_contents',
  'linkedin_contents',
  'twitter_contents',
  'tiktok_contents',
  'seo_contents',
  'email_contents',
  'youtube_contents',
  'google_my_business_contents',
]

const crosspostTactics = [
  BrandPlanTactic.Twitter,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.Instagram,
  BrandPlanTactic.GoogleBusinessProfile,
]

const videoCrosspostTactics = [BrandPlanTactic.InstagramReel, BrandPlanTactic.YouTube, BrandPlanTactic.TikTok]

const crosspostContentFields: (keyof Omit<
  FormValues,
  | 'crosspost_frequency'
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'tiktok_contents'
  | 'youtube_contents'
  | 'tactics'
>)[] = ['crosspost_contents', 'video_crosspost_contents', 'instagram_contents', 'seo_contents', 'email_contents']

const contentItemSchema: yup.ObjectSchema<ContentCount> = yup.object().shape({
  type: yup.string().required(),
  count: yup
    .number()
    .required('Count is required')
    .min(0)
    .test('dynamic-min', 'Total count must be at least 1', function (value) {
      const parent = this.parent as ContentCount
      const formValues = this.options?.context as FormValues | undefined

      if (!formValues) return true

      const crosspostFrequency = formValues['crosspost_frequency']

      if (crosspostFrequency === 'always') return true

      let totalOtherCounts = 0

      contentFields.forEach((field) => {
        const arr = formValues[field] ?? []
        arr.forEach((item) => {
          if (item !== parent) {
            totalOtherCounts += item.count ?? 0
          }
        })
      })

      if (totalOtherCounts === 0) {
        return (value ?? 0) >= 1
      }

      return true
    }),
  account_ids: yup.array().of(yup.number().required()).optional(),
  content_type: yup.string().oneOf(['still_image', 'video', 'carousel']).nullable().optional(),
  tactics: yup
    .array()
    .of(yup.string().oneOf(Object.values(BrandPlanTactic)).required())
    .optional(),
  week_days: yup.mixed<WeekDays>().optional(),
})

const crosspostContentItemSchema: yup.ObjectSchema<ContentCount> = yup.object().shape({
  type: yup.string().required(),
  count: yup
    .number()
    .required('Count is required')
    .min(0)
    .test('dynamic-min', 'Total count must be at least 1', function (value) {
      const parent = this.parent as ContentCount
      const formValues = this.options?.context as FormValues | undefined

      if (!formValues) return true

      const crosspostFrequency = formValues['crosspost_frequency']

      if (crosspostFrequency === 'never') return true

      let totalOtherCounts = 0

      crosspostContentFields.forEach((field) => {
        const arr = formValues[field] ?? []
        arr.forEach((item) => {
          if (item !== parent) {
            totalOtherCounts += item.count ?? 0
          }
        })
      })

      if (totalOtherCounts === 0) {
        return (value ?? 0) >= 1
      }

      return true
    }),
  account_ids: yup.array().of(yup.number().required()).optional(),
  content_type: yup.string().oneOf(['still_image', 'video', 'carousel']).nullable().optional(),
  tactics: yup
    .array()
    .of(yup.string().oneOf(Object.values(BrandPlanTactic)).required())
    .optional(),
  week_days: yup.mixed<WeekDays>().optional(),
})

const tacticsSchema = yup
  .array()
  .of(yup.string().oneOf(Object.values(BrandPlanTactic)))
  .required()
  .test('dynamic-min', 'You must pick at least one platform', function (value) {
    const formValues = this.options?.context as FormValues | undefined

    if (!formValues) return true

    const crosspostFrequency = formValues['crosspost_frequency']
    const stories = formValues['instagram_contents'].find((content) => content.type === 'story')?.count || 0

    if (crosspostFrequency === 'never') return true

    const chosenVideoCrosspostTactics = formValues['tactics'].filter((tactic) => videoCrosspostTactics.includes(tactic))
    const chosenCrosspostTactics = formValues['tactics'].filter((tactic) => crosspostTactics.includes(tactic))

    const crosspostContents = formValues['crosspost_contents']
    const videoCrosspostContents = formValues['video_crosspost_contents']

    if (crosspostContents[0].count > 0 && chosenCrosspostTactics.length < 1 && stories < 1) {
      return false
    }

    if (videoCrosspostContents[0].count > 0 && chosenVideoCrosspostTactics.length < 1 && stories < 1) {
      return false
    }

    return (value.length ?? 0) >= 1 || stories > 0
  })

export const validationSchema: yup.ObjectSchema<Omit<FormValues, 'crosspost_frequency' | 'tactics'>> = yup
  .object()
  .shape({
    instagram_contents: yup.array().of(contentItemSchema).required(),
    facebook_contents: yup.array().of(contentItemSchema).required(),
    linkedin_contents: yup.array().of(contentItemSchema).required(),
    twitter_contents: yup.array().of(contentItemSchema).required(),
    tiktok_contents: yup.array().of(contentItemSchema).required(),
    seo_contents: yup.array().of(contentItemSchema).required(),
    email_contents: yup.array().of(contentItemSchema).required(),
    youtube_contents: yup.array().of(contentItemSchema).required(),
    crosspost_contents: yup.array().of(crosspostContentItemSchema).required(),
    video_crosspost_contents: yup.array().of(crosspostContentItemSchema).required(),
    google_my_business_contents: yup.array().of(contentItemSchema).required(),
    tactics: tacticsSchema,
  })
